import { useId } from 'react';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { cardSurface } from '@/components/atoms/Card';
import type {
    ClarifyCatalog,
    PenRef,
    ProviderFieldSchema,
    ProviderGroup,
    ProviderRow,
} from '@/domain/providerConfig';
import { penKey, toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import { ProviderSchemaField } from '@/routes/shared/ProviderConfig/ProviderSchemaField';
import { unreadableAt } from '@/routes/shared/ProviderConfig/schemaFields';

import type { FieldConflict } from '../form/conflictMerge';
import type { ProviderFormValues } from '../form/formModel';

import { ClarifyItemPicker } from './ClarifyItemPicker';
import { PenCredentials } from './PenCredentials';
import { PenHealth } from './PenHealth';
import { PenRowHeader } from './PenRowHeader';
import { PenSignalFields } from './PenSignalFields';
import { conflictAt, type PenSecretField, SIGNAL_PREFIX } from './sectionFields';

import rowStyles from './penRow.module.scss';
import styles from './sections.module.scss';

const DESCRIPTION_KEY = 'description';

export interface PenMappingRowProps {
    pen: PenRef;
    row: ProviderRow | null;
    group: ProviderGroup | null;
    /** The pen-scope fields the viewer may edit. */
    fields: ProviderFieldSchema[];
    secrets: PenSecretField[];
    control: Control<ProviderFormValues>;
    setValue: UseFormSetValue<ProviderFormValues>;
    isOptoScaleAdmin: boolean;
    catalog: ClarifyCatalog | null;
    onFetchHistory: (pen: PenRef) => void;
    conflicts: FieldConflict[];
}

/** One pen: its header, health line and, while it is connected, its fields. */
function PenMappingRow({
    pen,
    row,
    group,
    fields,
    secrets,
    control,
    setValue,
    isOptoScaleAdmin,
    catalog,
    onFetchHistory,
    conflicts,
}: PenMappingRowProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const base: `pens.${string}` = `pens.${penKey(pen.cageId)}`;
    const [connected, remove, isActive] = useWatch({
        control,
        name: [`${base}.connected`, `${base}.remove`, `${base}.isActive`],
    });
    const signalFields = fields.filter((field) => field.key.startsWith(SIGNAL_PREFIX));
    // The mapping a pen cannot do without comes first, the optional SCADA list after it and
    // the admin's note last.
    const others = fields.filter((field) => !field.key.startsWith(SIGNAL_PREFIX));
    const mainFields = [
        ...others.filter((field) => field.key !== DESCRIPTION_KEY),
        ...others.filter((field) => field.key === DESCRIPTION_KEY),
    ];
    const editing = connected && !remove;
    // The tile's left accent says the pen's state at a glance; a pen not connected is the
    // grey tile with the plain edge.
    const state = !connected
        ? undefined
        : remove
          ? rowStyles.removing
          : isActive
            ? rowStyles.active
            : rowStyles.inactive;

    return (
        <li
            {...cardSurface(
                { variant: connected ? 'surface' : 'subtle', radius: 'sm', accent: 'stripe' },
                classNames(rowStyles.penRow, state)
            )}>
            <PenRowHeader id={id} pen={pen} row={row} base={base} control={control} />
            {conflictAt(conflicts, base) && (
                // The conflict toast says what happened; this marks where.
                <Alert tone="warning" size="small" live="off">
                    {t('providerConfig.conflict.pen')}
                </Alert>
            )}
            {row !== null && (
                <PenHealth
                    row={row}
                    action={
                        isOptoScaleAdmin && (
                            <Button variant="secondary" onClick={() => onFetchHistory(pen)}>
                                {t('providerConfig.pens.fetchHistory')}
                            </Button>
                        )
                    }
                />
            )}
            {editing && (
                <div className={classNames(styles.fields, rowStyles.penFields)}>
                    <PenSignalFields
                        base={base}
                        row={row}
                        signalFields={signalFields}
                        control={control}
                        catalog={catalog}
                        conflicts={conflicts}
                        showKeys={isOptoScaleAdmin}
                    />
                    {mainFields.map((field) => {
                        const name: `pens.${string}.fields.${string}` = `${base}.fields.${toFormKey(field.key)}`;
                        return (
                            <ProviderSchemaField<ProviderFormValues>
                                key={field.key}
                                field={field}
                                control={control}
                                name={name}
                                conflict={conflictAt(conflicts, name)}
                                unreadable={unreadableAt(row, field.key)}
                                itemPicker={
                                    catalog
                                        ? (binding) => (
                                              <ClarifyItemPicker
                                                  items={catalog.items}
                                                  {...binding}
                                              />
                                          )
                                        : undefined
                                }
                            />
                        );
                    })}
                    <PenCredentials
                        base={base}
                        row={row}
                        group={group}
                        secrets={secrets}
                        control={control}
                        setValue={setValue}
                    />
                </div>
            )}
        </li>
    );
}

export default PenMappingRow;
export { PenMappingRow };
